/**
 * Requirement (b) — "stop sharing kids' quiz results data with Meta pixel".
 *
 * Unconditional: this has nothing to do with the consent banner and applies to
 * every visitor, consented or not. The two gates are independent by design, so
 * the child gate cannot quietly come to depend on the consent one.
 *
 * ## What was leaking
 *
 * Thea's quiz is answered by an adult gift-giver *about* a recipient, and the
 * recipient is frequently a child (`KID_AGE_CHIPS` runs Newborn → High
 * School). Three fires carried that child's profile to `window.fbq`:
 *
 *   - `QuizSearchSubmitted` — `{occasion, relationship, gender, age_range}`
 *     where `age_range` was `'under_13'` or `'teen'`;
 *   - `QuizResultsViewed` — the same quartet, replayed on the results page;
 *   - `ViewContent` — the products recommended *for that child*, plus
 *     `content_category`, which is the carousel title. Carousel titles are
 *     generated backend-side and routinely describe the recipient
 *     ("Gifts for a 4-year-old", "For your Granddaughter").
 *
 * ## The invariant this module enforces
 *
 * **Nothing derived from a child recipient reaches `window.fbq`.** Not the
 * quiz answers, not the recommended products, not the carousel title. It is
 * enforced at the `metaPixel.ts` wrapper — the single chokepoint every Meta
 * fire already flows through — rather than at the seven call sites, so a new
 * surface cannot reintroduce the leak by forgetting a check.
 *
 * ## Withholding the attributes is not the same as withholding the count
 *
 * The original implementation enforced the invariant by dropping the whole
 * `QuizResultsViewed` fire, which also removed the *fact that a quiz was
 * completed* — so Meta could not count child-recipient completions, optimize
 * toward them, or build a quiz-completer audience from them. That was a side
 * effect of the mechanism, not the requirement (Kate Rowe, 2026-09-11): the
 * requirement is about child *data*, and a bare completion count is not data
 * about the child.
 *
 * So `QuizResultsViewed` now always fires, and `minimizeChildQuizParams()`
 * decides what it may carry when a child is (or may be) the recipient. See
 * that function for why the answer is "nothing at all" rather than "the
 * aggregate counts". `QuizSearchSubmitted` is deliberately NOT changed — Kate
 * named the funnel-completion event only, and the mid-funnel one has no
 * separate counting value that would justify reopening the question.
 *
 * ## Server-side CAPI
 *
 * CAPI keeps firing, per the explicit product decision that it stays live.
 * `content_category` is stripped from its payload too, because that string is
 * the one field that can describe the child in prose; `content_ids` /
 * `content_name` (catalog identifiers for an item the *adult* clicked) still
 * go. That is a narrower line than the Pixel's, and it is a deliberate
 * difference: the requirement names the Pixel, and CAPI is Thea's measurement
 * backbone. It is flagged for legal review — a lawyer may want CAPI held to
 * the same standard, which would be a one-line change in `metaPixel.ts`.
 */

/**
 * Ages below this are treated as children.
 *
 * `KID_AGE_CHIPS` tops out at 16 ("High School", 14–18 years) and
 * `ADULT_AGE_CHIPS` starts at 25, so 18 separates them with room on both
 * sides. `RecommendationResultsPage` already used a bare `age < 18` inline;
 * this is that rule, named and centralized.
 *
 * `childAudience.test.ts` asserts the threshold against the real chip tables,
 * so a future chip change that crosses this line fails the build.
 */
export const CHILD_AGE_THRESHOLD = 18;

/**
 * The `ageBucket()` outputs that denote a child. Kept as data (rather than a
 * numeric re-derivation) because the quiz-event params carry the *bucketed*
 * string, not the raw age.
 */
export const CHILD_AGE_BUCKETS: readonly string[] = ['under_13', 'teen'];

/**
 * Who the Meta fire is about.
 *
 * A three-state discriminated union rather than `number | undefined`, because
 * "no recipient is involved" (marketing pages, gift guides, the teacher
 * landing page) and "a recipient is involved but we don't know their age" are
 * genuinely different, and only the second one should fail safe to child.
 */
export type RecipientAudience =
  /** No quiz recipient in scope — marketing, guide and landing surfaces. */
  | { kind: 'no-recipient' }
  /** A quiz recipient is in scope. `age` may be unknown. */
  | { kind: 'recipient'; age: number | null | undefined };

/** Shared constant so call sites don't allocate a fresh object per fire. */
export const NO_RECIPIENT: RecipientAudience = { kind: 'no-recipient' };

/** Build an audience from a (possibly missing) recipient age. */
export function recipientAudience(
  age: number | null | undefined,
): RecipientAudience {
  return { kind: 'recipient', age };
}

/**
 * True when `age` should be treated as a child.
 *
 * FAIL-SAFE: a missing, null or non-finite age counts as a child. We cannot
 * prove a recipient is an adult, so an unknown age is withheld. `0` is the
 * quiz's "unanswered" sentinel and lands on the same side for the same reason.
 *
 * The cost is real and accepted: any quiz whose age never got recorded stops
 * reporting to the Pixel, which trims Meta's optimization signal. Suppressing
 * an adult by mistake is a measurement loss; sharing a child by mistake is a
 * compliance failure.
 */
export function isChildAge(age: number | null | undefined): boolean {
  if (age === null || age === undefined) return true;
  if (!Number.isFinite(age)) return true;
  return age < CHILD_AGE_THRESHOLD;
}

/** True when a bucketed `age_range` denotes a child — including when absent. */
export function isChildAgeBucket(bucket: string | null | undefined): boolean {
  if (bucket === null || bucket === undefined) return true;
  return CHILD_AGE_BUCKETS.indexOf(bucket) !== -1;
}

/** True when this audience must be withheld from the Meta Pixel. */
export function isChildAudience(audience: RecipientAudience): boolean {
  return audience.kind === 'recipient' && isChildAge(audience.age);
}

/**
 * Strip the fields that can describe a child from a Meta payload.
 *
 * Only `content_category` qualifies today: it is the backend-generated
 * carousel title on the results board, which is written about the recipient.
 * On every other surface it is a static string ('liked', 'shared_board', a
 * guide title) and there is no child in scope to strip for anyway.
 */
export function stripChildDerivedFields<T extends { content_category?: string }>(
  params: T,
): T {
  if (params.content_category === undefined) return params;
  const next = { ...params };
  delete next.content_category;
  return next;
}

/**
 * The quiz-event params that may survive minimization — an ALLOWLIST, and it
 * is empty.
 *
 * Every field `QuizResultsViewedParams` carries is derived from the child or
 * from what we recommended for the child: `age_range` and `relationship`
 * describe them directly, `interest_count` counts their interests, and
 * `carousel_count` / `product_count` count the board built *for* them. The
 * counts are the arguable ones — they are integers about the board, not
 * strings about the kid — but they are still a per-child attribute (how many
 * shelves and items this particular child's results filled), and Kate's
 * exclusion list names "product/category details" explicitly. Keeping them
 * would buy Meta nothing she asked for: the count, the optimization signal and
 * the retargeting audience all come from the event *existing*, not from its
 * payload.
 *
 * Note what is also absent: any marker distinguishing a minimized fire from a
 * normal one (`data_minimized: true`, `child_recipient: true`, or an empty
 * `age_range: ''`). Such a flag would appear on exactly the child-recipient
 * and unknown-age quizzes and on no others, which makes it a one-bit
 * disclosure of the recipient's age band — the precise thing this module
 * exists to withhold.
 *
 * Be precise about what omitting the marker does and does not buy, because a
 * legal review will rest on this paragraph. It does NOT make the minimized
 * fire indistinguishable from an adult one. An adult quiz cannot be fired with
 * an empty bag: `interest_count` (`?? 0`), `carousel_count` (`sections.length`)
 * and `product_count` (a reduce) are computed unconditionally at the call site
 * in `RecommendationResultsPage`, and `age_range` is always defined for an
 * adult — an `undefined` bucket routes here by construction. The leanest
 * possible adult session (no occasion, no relationship, no interests, one empty
 * carousel) still sends four defined keys, and the `carousel_count === 0`
 * escape does not exist because the effect early-returns before firing
 * anything. Measured on 2026-09-11 (THE-327 QA). So on the wire, an empty
 * custom-data object occurs IFF the recipient is a child or has no recorded
 * age.
 *
 * We accept that one bit and withhold every attribute. The bit is not new —
 * `PageView` fires unconditionally on `/quiz/results/...`, so before this
 * change "a results PageView with no `QuizResultsViewed` after it" carried the
 * same bit inverted; any age-conditional behaviour has this property. What the
 * omitted marker does buy is that the bit stays an inference from a payload
 * shape rather than a named field Meta can key a custom audience on directly.
 *
 * This is the list to edit if that decision changes, and
 * `childDataNeverReachesPixel.test.ts` asserts it stays empty so the change is
 * a deliberate one.
 */
export const CHILD_SAFE_QUIZ_PARAM_KEYS: readonly string[] = [];

/**
 * Reduce a quiz-event param bag to the child-safe allowlist.
 *
 * Allowlist, not denylist, on purpose: a field added to
 * `QuizResultsViewedParams` later is withheld by default rather than shipped
 * by default, so a future param cannot reopen the leak by being forgotten
 * here. With the list empty this always returns `{}` — a FRESH object each
 * call, never a shared constant, so nothing downstream can mutate the policy
 * by mutating what it was handed.
 */
export function minimizeChildQuizParams<T extends object>(params: T): Partial<T> {
  const source = params as Record<string, unknown>;
  const next: Record<string, unknown> = {};
  CHILD_SAFE_QUIZ_PARAM_KEYS.forEach((key) => {
    if (source[key] !== undefined) next[key] = source[key];
  });
  return next as Partial<T>;
}
